import { CSSProperties, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import styled from 'styled-components';
import { Hymn } from '../../../common/src/model.ts';
import { SongOptionsSetting } from '../pages/settings/song_options.tsx';
import { Kebab, kebabIconBarHeightPx } from '../util/kebab.tsx';
import './slide_menus.css';
import { SongInfo } from './song_info.tsx';

interface Props {
  show: boolean;
  hymn: Hymn | undefined;
  close: () => void;
  showModal: () => void;
  initiallyExpandedKebabSetting?: SongOptionsSetting;
}

enum Menu {
  Kebab,
  Info,
}

// The class of both sheets; the player's swipe handling finds a sheet by it
export const slideMenuClassName = 'slideMenu';

export const SlideMenu = ({ show, hymn, close, showModal, initiallyExpandedKebabSetting }: Props) => {
  const [display, setDisplay] = useState(Menu.Kebab);
  const [landscape, setLandscape] = useState(isLandscapeViewport);

  useEffect(() => {
    const controller = new AbortController();
    const handleResize = () => setLandscape(isLandscapeViewport());
    window.addEventListener('resize', handleResize, { signal: controller.signal });
    return () => controller.abort();
  }, []);

  const displaySongInfo = useCallback(() => {
    setDisplay(Menu.Info);
  }, []);

  const onClickBackground = useCallback(
    (event: any) => {
      close();
      setDisplay(Menu.Kebab);
      event.stopPropagation(); // prevent mixer from closing
    },
    [close],
  );

  const onBack = useCallback(() => {
    setDisplay(Menu.Kebab);
  }, []);

  const isKebabShown = show && display === Menu.Kebab;
  const isInfoShown = show && display === Menu.Info;
  const kebabSheetRef = useRef<HTMLDivElement>(null);
  const infoSheetRef = useRef<HTMLDivElement>(null);
  useScrollToTopWhenHidden(kebabSheetRef, isKebabShown);
  useScrollToTopWhenHidden(infoSheetRef, isInfoShown);

  return (
    <>
      <SlideMenuBackground $show={show} onClick={onClickBackground} />
      {/* Sized to its content (capped at 90% of the player by .slideMenu), so it grows and shrinks with
          an expanding option panel instead of cutting it off (GitHub issue #3608). Its scroll padding is the
          icon bar pinned over its top, which a panel's reveal scroll keeps its row clear of. */}
      <div
        ref={kebabSheetRef}
        className={slideMenuClassName}
        style={{ scrollPaddingTop: kebabIconBarHeightPx, ...getSlideStyle(isKebabShown) }}
      >
        {hymn && (
          <div className="wrapper">
            <Kebab
              hymn={hymn}
              displaySongInfo={displaySongInfo}
              displayModal={showModal}
              close={close}
              initiallyExpandedSetting={initiallyExpandedKebabSetting}
            />
          </div>
        )}
      </div>
      <div
        ref={infoSheetRef}
        className={slideMenuClassName}
        style={{
          top: landscape && !isDesktop ? '30%' : '45%',
          ...getSlideStyle(isInfoShown),
        }}
      >
        {hymn && (
          <div className="wrapper">
            <SongInfo back={onBack} hymn={hymn} />
          </div>
        )}
      </div>
    </>
  );
};

function isLandscapeViewport(): boolean {
  return window.innerHeight < window.innerWidth;
}

// A hidden sheet only slides out of view and keeps its scroll position, so without this it would reopen scrolled,
// with its first rows under its pinned top bar
function useScrollToTopWhenHidden(ref: RefObject<HTMLDivElement | null>, visible: boolean) {
  useEffect(() => {
    if (visible || !ref.current) {
      return;
    }
    ref.current.scrollTop = 0;
  }, [ref, visible]);
}

// A hidden sheet sits just below the player, whose overflow: hidden clips it; it slides up when shown
// and drops away at once when hidden
function getSlideStyle(visible: boolean): CSSProperties {
  return visible ? { transform: 'translateY(0)', transition: 'transform 250ms' } : { transform: 'translateY(100%)' };
}

const SlideMenuBackground = styled.div<{ $show: boolean }>`
  z-index: ${({ $show }) => ($show ? '190' : '-20')};
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${({ $show }) =>
    $show &&
    `
    background-color: var(--color-background-modal-overlay);
    transition: background-color 200ms ease-in-out;
  `};
`;
